import React, { useEffect, useState } from 'react';
import {
  Box, Stack, Typography, Paper, alpha, CircularProgress,
  Divider, Tooltip, IconButton,
} from '@mui/material';
import {
  SupportAgent, CheckCircle, Cancel, ContentCopy,
  AccessTime, FeedbackOutlined,
} from '@mui/icons-material';
import {
  collection, query, where, orderBy, limit, getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { tokens } from '../theme';
import { SoftChip } from './SoftChip';
import type { TenantConsoleListItem } from '../services/tenantService';

interface Props {
  tenant: TenantConsoleListItem;
  onChange: () => Promise<void>;
}

interface MissingFeatureEvent {
  id: string;
  timestamp: Timestamp;
  display_name: string;
  metadata?: { kind?: string; display_name?: string };
  details?: Record<string, unknown>;
}

function fmtTs(ts: Timestamp | { seconds: number } | undefined): string {
  if (!ts) return '—';
  const ms = 'toMillis' in ts ? ts.toMillis() : ts.seconds * 1000;
  return new Date(ms).toLocaleString('it-IT', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export const TenantYumaCallTab: React.FC<Props> = ({ tenant }) => {
  const [events, setEvents] = useState<MissingFeatureEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const modules = tenant.modules ?? {};
  const base = modules.base ?? [];
  const addon = modules.addon ?? [];
  const expired = modules.expired ?? [];
  const override = (modules as Record<string, string[]>).override ?? [];

  const isActive = !expired.includes('yumacall') &&
    (base.includes('yumacall') || addon.includes('yumacall') || override.includes('yumacall'));
  const isInbound = !expired.includes('yumacall_inbound') &&
    (base.includes('yumacall_inbound') || addon.includes('yumacall_inbound') || override.includes('yumacall_inbound'));

  useEffect(() => {
    (async () => {
      try {
        const q = query(
          collection(db, 'mkt_events'),
          where('tenant_id', '==', tenant.id),
          where('event_type', '==', 'voice_assistant_missing_feature'),
          orderBy('timestamp', 'desc'),
          limit(20),
        );
        const snap = await getDocs(q);
        setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() } as MissingFeatureEvent)));
      } catch (err) {
        console.error('Errore caricamento eventi YumaCall:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [tenant.id]);

  const ACCENT = '#0D9488';

  return (
    <Stack spacing={2.5}>
      {/* Hero status */}
      <Paper
        variant="outlined"
        sx={{
          p: 0, overflow: 'hidden',
          borderColor: isActive ? alpha(ACCENT, 0.35) : tokens.color.line,
        }}
      >
        <Box
          sx={{
            px: 3, py: 2.5,
            background: isActive
              ? `linear-gradient(135deg, ${alpha(ACCENT, 0.06)} 0%, ${alpha(ACCENT, 0.02)} 100%)`
              : alpha(tokens.color.mist, 0.06),
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48, height: 48,
                borderRadius: tokens.radius.md,
                bgcolor: isActive ? alpha(ACCENT, 0.12) : alpha(tokens.color.mist, 0.12),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SupportAgent sx={{ fontSize: 26, color: isActive ? ACCENT : tokens.color.mist }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, letterSpacing: '-0.01em', color: tokens.color.graphite }}
              >
                YumaCALL
              </Typography>
              <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.25 }}>
                Assistente AI per gestione lead e follow-up commerciale
              </Typography>
            </Box>
            <SoftChip
              label={isActive ? 'Attivo' : 'Non attivo'}
              tone={isActive ? 'success' : 'default'}
              icon={isActive ? <CheckCircle /> : <Cancel />}
            />
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ px: 3, py: 2 }}>
          <Stack direction="row" spacing={4} flexWrap="wrap" rowGap={1.5}>
            <CapRow
              label="Modulo"
              capId="yumacall"
              active={isActive}
              source={
                base.includes('yumacall') ? 'base (tier)' :
                addon.includes('yumacall') ? 'addon' :
                override.includes('yumacall') ? 'override' : '—'
              }
            />
            <CapRow
              label="Inbound API"
              capId="yumacall_inbound"
              active={isInbound}
              source={
                base.includes('yumacall_inbound') ? 'base (tier)' :
                addon.includes('yumacall_inbound') ? 'addon' : '—'
              }
            />
          </Stack>
        </Box>
      </Paper>

      {/* Feedback log */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <FeedbackOutlined sx={{ fontSize: 20, color: tokens.color.ash }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Funzionalita' richieste
          </Typography>
          <SoftChip label={`${events.length}`} tone="default" mono />
        </Stack>

        <Typography variant="body2" sx={{ color: tokens.color.ash, mb: 2 }}>
          Richieste non supportate segnalate automaticamente dai commerciali durante le sessioni vocali.
        </Typography>

        {loading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : events.length === 0 ? (
          <Typography variant="body2" sx={{ color: tokens.color.mist, py: 3, textAlign: 'center' }}>
            Nessuna segnalazione per questo tenant
          </Typography>
        ) : (
          <Stack spacing={0} divider={<Divider />}>
            {events.map(ev => (
              <Box
                key={ev.id}
                sx={{
                  py: 1.5,
                  '&:hover': { bgcolor: alpha(tokens.color.graphite, 0.02) },
                  transition: 'background 150ms',
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                  <AccessTime sx={{ fontSize: 16, color: tokens.color.mist, mt: 0.25, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: tokens.color.graphite }}
                      >
                        {ev.metadata?.display_name || ev.display_name || '—'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: tokens.color.mist, fontFamily: tokens.font.mono, fontSize: '0.68rem' }}
                      >
                        {fmtTs(ev.timestamp)}
                      </Typography>
                    </Stack>
                    {ev.details && (
                      <Typography
                        variant="body2"
                        sx={{ color: tokens.color.ash, mt: 0.5, lineHeight: 1.5 }}
                      >
                        {String((ev.details as Record<string, unknown>).user_request ?? '')}
                      </Typography>
                    )}
                  </Box>
                  <Tooltip title="Copia ID evento">
                    <IconButton
                      size="small"
                      onClick={() => navigator.clipboard.writeText(ev.id)}
                      sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }}
                    >
                      <ContentCopy sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
};

const CapRow: React.FC<{
  label: string;
  capId: string;
  active: boolean;
  source: string;
}> = ({ label, capId, active, source }) => (
  <Box>
    <Typography
      variant="overline"
      sx={{ display: 'block', mb: 0.25 }}
    >
      {label}
    </Typography>
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Typography
        variant="body2"
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: '0.78rem',
          color: active ? tokens.color.graphite : tokens.color.mist,
        }}
      >
        {capId}
      </Typography>
      <SoftChip
        label={active ? source : 'off'}
        tone={active ? 'success' : 'default'}
        mono
      />
    </Stack>
  </Box>
);
